import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from '../contexts/TranslationContext';
import {
  BriefcaseIcon,
  UserGroupIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

/* ── Scroll-reveal wrapper ─────────────────────────────────────────────────── */
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ── Check-mark icon ───────────────────────────────────────────────────────── */
const Check = ({ variant = 'blue' }) => {
  const styles = {
    blue:  { bg: 'bg-blue-100',  text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
  };
  const s = styles[variant] || styles.blue;
  return (
    <span className={`flex-shrink-0 w-5 h-5 rounded-full ${s.bg} flex items-center justify-center mr-3`}>
      <svg className={`w-3 h-3 ${s.text}`} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
      </svg>
    </span>
  );
};

/* ── Landing Page ──────────────────────────────────────────────────────────── */
const LandingPage = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>Cogent Platform — Managed Investment Solutions</title>
        <meta name="description" content="Cogent Asset Management Ltd offers professionally managed portfolios, IFA corporate finance, and Labuan foundation services. Licensed and regulated under the Labuan Financial Services & Securities Act 2010." />
        <link rel="canonical" href="https://cogentplatform.com/" />
      </Helmet>
      <Navbar />

      <main className="flex-grow">
        {/* ── HERO ─────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0f2447 40%, #0c3d6b 70%, #0e4a7a 100%)' }}>
          {/* Subtle dot-grid pattern */}
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} />
          {/* Ambient glow accents */}
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-blue-400/8 rounded-full blur-3xl" />

          <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-24 text-center">
            <Reveal>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
                {t('landing.hero.title')}
              </h1>
            </Reveal>

            <Reveal delay={120}>
              <p className="mt-4 text-base sm:text-lg md:text-xl text-blue-100/80 leading-relaxed max-w-2xl mx-auto">
                {t('landing.hero.subtitle')}
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-white text-blue-900 font-semibold rounded-lg shadow-lg hover:shadow-xl hover:bg-blue-50 transition-all duration-200"
                >
                  {t('landing.hero.accessPortfolio')}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-200"
                >
                  {t('landing.hero.contactExpert')}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── TRUST INDICATORS ─────────────────────────────────────────────── */}
        <section className="py-14 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
              {[
                { icon: ShieldCheckIcon, label: t('landing.trust.regulated'),   sub: t('landing.trust.regulatedSub') },
                { icon: LockClosedIcon,  label: t('landing.trust.secure'),      sub: t('landing.trust.secureSub') },
                { icon: ChartBarIcon,    label: t('landing.trust.transparent'), sub: t('landing.trust.transparentSub') },
                { icon: SparklesIcon,    label: t('landing.trust.modern'),      sub: t('landing.trust.modernSub') },
              ].map(({ icon: Icon, label, sub }, i) => (
                <Reveal key={i} delay={i * 100} className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg mb-3">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{label}</h3>
                  <p className="text-sm text-gray-500 mt-1">{sub}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── FOR ADVISORS & CLIENTS ───────────────────────────────────────── */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">

              {/* For Advisors */}
              <Reveal>
                <div className="group h-full bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-blue-200 transition-all duration-300 overflow-hidden">
                  <div className="h-1.5 bg-gradient-to-r from-blue-600 to-cyan-500" />
                  <div className="p-8 sm:p-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                        <BriefcaseIcon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.forAdvisors.title')}</h2>
                        <p className="text-sm text-blue-600 font-semibold">{t('landing.forAdvisors.subtitle')}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 leading-relaxed mb-6">{t('landing.forAdvisors.description')}</p>

                    <ul className="space-y-3 mb-8">
                      {['feature1', 'feature2', 'feature3'].map(k => (
                        <li key={k} className="flex items-start text-sm text-gray-700">
                          <Check variant="blue" />
                          <span>{t(`landing.forAdvisors.${k}`)}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contact"
                      className="group/cta inline-flex items-center justify-center w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    >
                      {t('landing.forAdvisors.cta')}
                      <ArrowRightIcon className="ml-2 h-5 w-5 group-hover/cta:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>

              {/* For Clients */}
              <Reveal delay={150}>
                <div className="group h-full bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-green-200 transition-all duration-300 overflow-hidden">
                  <div className="h-1.5 bg-gradient-to-r from-green-600 to-emerald-500" />
                  <div className="p-8 sm:p-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-green-600 to-emerald-500 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300">
                        <UserGroupIcon className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.forClients.title')}</h2>
                        <p className="text-sm text-green-600 font-semibold">{t('landing.forClients.subtitle')}</p>
                      </div>
                    </div>

                    <p className="text-gray-600 leading-relaxed mb-6">{t('landing.forClients.description')}</p>

                    <ul className="space-y-3 mb-8">
                      {['feature1', 'feature2', 'feature3'].map(k => (
                        <li key={k} className="flex items-start text-sm text-gray-700">
                          <Check variant="green" />
                          <span>{t(`landing.forClients.${k}`)}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/login"
                      className="group/cta inline-flex items-center justify-center w-full px-6 py-3.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                    >
                      {t('landing.forClients.cta')}
                      <ArrowRightIcon className="ml-2 h-5 w-5 group-hover/cta:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>

            </div>
          </div>
        </section>

        {/* ── WHY CHOOSE COGENT ────────────────────────────────────────────── */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('landing.whyChoose.title')}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
                  {t('landing.whyChoose.titleHighlight')}
                </span>
              </h2>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                {t('landing.whyChoose.subtitle')}
              </p>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                { icon: ChartBarIcon,      titleKey: 'feature1Title', descKey: 'feature1Desc' },
                { icon: ShieldCheckIcon,   titleKey: 'feature2Title', descKey: 'feature2Desc' },
                { icon: CurrencyDollarIcon, titleKey: 'feature3Title', descKey: 'feature3Desc' },
              ].map(({ icon: Icon, titleKey, descKey }, i) => (
                <Reveal key={i} delay={i * 120}>
                  <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-xl p-8 border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all duration-300">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center mb-5 shadow-md">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{t(`landing.whyChoose.${titleKey}`)}</h3>
                    <p className="text-gray-600 leading-relaxed">{t(`landing.whyChoose.${descKey}`)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
        <section className="py-16" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0f2447 50%, #0e4a7a 100%)' }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {t('landing.cta.title')}
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <p className="text-base md:text-lg text-blue-100/80 mb-8 max-w-2xl mx-auto">
                {t('landing.cta.subtitle')}
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-white text-blue-900 font-semibold rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                >
                  {t('landing.cta.login')}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-7 py-3.5 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-200"
                >
                  {t('landing.cta.schedule')}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;
